/**
 * Kiosk 品牌类型 — BrandedType 模式
 *
 * R1 自检：本文件严禁出现 dtu_id / dtu_code / serial / raw_hex 字段
 * 以上字段属于 DTU 物联网层，已由后端 PushMessageBuilder::stripDtuFields 剥离
 */

/** 品牌类型工具 */
type Brand<T, B extends string> = T & { readonly _brand: B }

/** 机器编号（格式：KIOSK-XXXXX） */
export type MachineCode = Brand<string, 'MachineCode'>

/** 门店 ID */
export type StoreId = Brand<number, 'StoreId'>

/** 订单 ID */
export type OrderId = Brand<number, 'OrderId'>

/** 商品 ID */
export type ProductId = Brand<number, 'ProductId'>

/** 分类 ID */
export type CategoryId = Brand<number, 'CategoryId'>

/** 广告 ID */
export type AdId = Brand<number, 'AdId'>

/** Kiosk 运营模式（E 期仅支持 CARDSHOP） */
export type KioskMode = 'CARDSHOP' | 'HYBRID'

/** 机器状态 */
export type MachineStatus = 'normal' | 'maintenance' | 'offline'

/** 创建 MachineCode（带运行时格式校验） */
export function toMachineCode(raw: string): MachineCode {
  if (!raw || raw.trim().length === 0) {
    throw new Error('MachineCode 不能为空')
  }
  return raw.trim() as MachineCode
}

/** 创建 OrderId */
export function toOrderId(id: number): OrderId {
  if (!Number.isInteger(id) || id <= 0) {
    throw new Error(`非法 OrderId: ${id}`)
  }
  return id as OrderId
}

/**
 * 机器档案（来自 GET /api/kiosk/profile）
 * 注意：此处不包含 ws_ticket / sig / hmac_secret 等敏感字段
 */
export interface MachineProfile {
  machine_code: MachineCode
  store_id: StoreId
  store_name: string
  store_phone: string
  kiosk_mode: KioskMode
  status: MachineStatus
  locale: string
  timezone: string
  business_hours: BusinessHours | null
}

export interface BusinessHours {
  open: string  // "09:00"
  close: string // "22:00"
  days: number[] // 0=周日 ~ 6=周六
}

// ─── E.4 新增实体类型 ──────────────────────────────────────────────────────────

/**
 * 订单实体（Kiosk 侧视图）
 * R1 自检：禁止包含 dtu_id / serial / raw_hex 字段
 */
export interface KioskOrder {
  id: OrderId
  orderNo: string
  machineCode: string
  productId: number
  /** 金额，单位：分（fen）。统一为分，方便 formatCurrency 处理 */
  amount: number
  /** 已支付金额，单位：分（fen）。后端 paid_amount 为元，前端乘以 100 换算 */
  paidAmount: number
  /** 币种代码，如 'TWD'/'CNY'/'USD'，来自后端 currency 字段，默认 'TWD' */
  currency: string
  status: 'PENDING' | 'IN_PAY' | 'PAYING' | 'PAID' | 'DELIVERING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'CANCELED' | 'FULFILLED' | 'TIMEOUT' | 'REFUNDING' | 'REFUNDED'
  createdAt: number
  updatedAt: number
  paidAt?: number
  cardNumber?: string
  cardKey?: string
  hasInvoice?: boolean
  invoiceNo?: string
  /** D2 新增：QR 核销 token（来自 payload 接口），用于生成核销二维码 */
  qrToken?: string
  /** D2 派生：完整核销 URL（/v/{qrToken}），base 来自 env VITE_PUBLIC_VERIFY_BASE */
  qrVerifyUrl?: string
  /** 订单过期时间（Unix timestamp 秒，来自后端 expires_at），用于前端 90s 倒计时显示 */
  expiresAt?: number
}

/**
 * 商品实体
 */
export interface KioskProduct {
  id: number
  name: string
  /** 售价，单位：分（fen）。后端 sale_price 为元，前端乘以 100 换算 */
  price: number
  /** 币种代码，如 'TWD'/'CNY'/'USD'，来自后端 currency 字段，默认 'TWD' */
  currency: string
  categoryId: number
  imageUrl: string
  description?: string
}

/**
 * 广告实体
 */
export interface KioskAd {
  id: number
  type: 'image' | 'video'
  url: string
  durationMs: number
  weight?: number
}
